import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useSwipeable } from 'react-swipeable';
import { CompleteBongQuery_venue_company_offers as Offer } from '~Scenes/POS/Queries/__queries__';

import NumberInput, { SelectedNumpadTab } from '~ComponentsImproved/Numpad';
import { useStaffCan, useI18n, useButton } from '~/Hooks';
import { DISCOUNT_TYPE } from '~../__queries__';
import { WhiteButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Icon from '~ComponentsImproved/Icon';
import CheckIcon from '~Assets/icons/check.svg';
import { useCurrency } from '~Hooks/useCurrency';
import { useCart } from '~Components/CartProvider';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';

const ButtonWrapper = styled.div<{
  fullWidth?: boolean;
}>`
  flex: 1;
  margin: 0 ${(props) => (props.fullWidth ? 0 : 4)}px;
  position: relative;
  min-width: 95%;

  button {
    position: relative;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  height: 100vh;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  background-color: rgb(14 18 22 / 75%);
  z-index: 2;
  animation: fade-in 0.2s forwards;
`;

const ListWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: -8px;
  transform: translateY(-100%);
  z-index: 3;
  animation: fade-in 0.2s forwards;
  right: 0;
`;

const ButtonsList = styled.div`
  border-radius: 4px;
  background-color: white;
  max-height: calc(100vh - 440px);
  overflow: auto;
  margin-top: 8px;
`;

const ButtonsListItem = styled.div<{ active: boolean; disabled?: boolean }>`
  height: 64px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${({ active }) => (active ? 'var(--colorBlueGray50)' : 'transparent')};

  ${({ disabled }) => disabled && 'opacity: 0.5;pointer-events: none;'}

  &:not(:last-child) {
    border-bottom: 1px solid var(--colorBlueGray200);
  }

  div {
    margin-left: 12px;
  }
`;

const DiscountButton = styled(WhiteButton)`
  font-size: 14px;
  min-height: 56px;
  width: 100%;
  display: block;
`;

interface Props {
  offers: Offer[];
  fullWidth?: boolean;
  /**
   * If set, the discount will be applied to the selected items.
   * If not, it applies to the whole cart
   */
  forSelectedItems?: boolean;
  disabled?: boolean;
}

const Discounts = ({ offers, forSelectedItems, fullWidth, disabled }: Props) => {
  const cart = useCart();
  const staffCan = useStaffCan();
  const [showNumpad, setShowNumpad] = useState<boolean>(false);
  const [showDiscounts, setShowDiscounts] = useState(false);
  const { i18n } = useI18n();
  const [touching, setTouching] = useState(false);
  const currency = useCurrency();

  const offersList = useMemo<Offer[]>(
    () => offers.filter((offer) => offer.type === 'ORDER_DISCOUNT' && offer.enabled),
    [offers],
  );

  const itemIdsForOffer = useMemo<Record<string, string[]>>(() => {
    return offersList.reduce((obj, offer) => {
      const ids = [
        ...(offer.categories
          .map((cat) => cat.items)
          .flat()
          .map((i) => i.id) ?? []),
        ...offer.items.map((i) => i.id),
      ];

      obj[offer.id] = ids;

      return obj;
    }, {} as Record<string, string[]>);
  }, [offersList]);

  /**
   * Filter offers which have specific itemIds and those
   * itemIds dont overlap with the selected items
   */
  /* const invalidOfferIds = useMemo<string[]>(() => {
    const selectedItemIds = cart.selectedItems.ids;

    return offers.reduce((arr, offer) => {
      const itemIds = itemIdsForOffer[offer.id];

      const isValid = itemIds.length === 0 || selectedItemIds.every((id) => itemIds.includes(id));

      if (!isValid) {
        arr.push(offer.id);
      }

      return arr;
    }, [] as string[]);
  }, [cart.selectedItems.ids, itemIdsForOffer, offers]); */

  const handleAddDiscount = (offer: Offer) => {
    if (forSelectedItems) {
      cart.selectedItems.applyDiscount({
        id: offer.id,
        type: offer.discountType,
        value: offer.discountRate,
        title: i18n(offer.nameLang),
      });
      cart.selectedItems.clear();
      return;
    }

    cart.setAppliedDiscount({
      id: offer.id,
      type: offer.discountType,
      value: offer.discountRate,
      itemIds: itemIdsForOffer[offer.id],
      title: i18n(offer.nameLang),
    });
  };

  const handleTouchEnd = (offer: Offer) => {
    if (touching) {
      if (offer.id === cart.appliedDiscount?.id) {
        if (forSelectedItems) {
          cart.selectedItems.setDiscount(0);
        } else {
          cart.setAppliedDiscount(null);
        }
      } else {
        handleAddDiscount(offer);
        setShowDiscounts(false);
      }
    }
  };

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => setTouching(false),
  });

  const showManualDiscount = () => {
    setShowNumpad(true);
    setShowDiscounts(false);
  };

  const handleManualDiscount = (value: string, tab: SelectedNumpadTab) => {
    if (!tab) {
      return;
    }
    let discountType = DISCOUNT_TYPE.PERCENTAGE;
    if (tab.type === 'freeform') {
      discountType = DISCOUNT_TYPE.PRICE;
    }
    setShowNumpad(false);

    const discount = {
      type: discountType,
      value: discountType == 'PRICE' ? Math.round(+value * 100) : parseInt(value),
    };

    if (forSelectedItems) {
      cart.selectedItems.applyDiscount(discount);
      cart.selectedItems.clear();
    } else {
      cart.setAppliedDiscount(discount);
    }
  };

  const handleDiscountButton = () => {
    //If there are no offers or if the staff is not allowed to use the offers go to manual discount
    if (offersList.length === 0 || !staffCan.PRESET_DISCOUNT) {
      showManualDiscount();
    } else {
      setShowDiscounts(!showDiscounts);
    }
  };

  // Disables the button if there are no offers and the staff cannot do manual discount OR the staff is not allowed too give discounts
  const discountDisabled =
    (offersList.length === 0 && !staffCan.MANUAL_DISCOUNT) ||
    (!staffCan.MANUAL_DISCOUNT && !staffCan.PRESET_DISCOUNT);

  const hasRemoveableDiscount = useMemo(() => {
    if (forSelectedItems) {
      return cart.selectedItems.items.some((item) => item.discount > 0);
    }

    return false;
  }, [cart.selectedItems.items, forSelectedItems]);

  const discountButton = useButton({
    disabled: discountDisabled || disabled,
    onClick: handleDiscountButton,
  });

  const manualDiscount = useButton({
    onClick: showManualDiscount,
  });

  return (
    <>
      <ButtonWrapper
        fullWidth={fullWidth ?? forSelectedItems}
        style={{
          zIndex: showDiscounts ? 3 : 1,
        }}>
        {showDiscounts && <Backdrop onClick={() => setShowDiscounts(false)} />}
        <DiscountButton
          {...discountButton}
          style={{
            zIndex: showDiscounts ? 3 : 1,
          }}>
          {showDiscounts ? i18n('POS.Cancel').toUpperCase() : i18n('POS.Discount').toUpperCase()}
        </DiscountButton>
        {showDiscounts && (
          <ListWrapper>
            {staffCan.MANUAL_DISCOUNT && (
              <DiscountButton {...manualDiscount}>{i18n('POS.ManualDiscount')}</DiscountButton>
            )}
            <ButtonsList {...handlers}>
              {cart.appliedDiscount && !cart.appliedDiscount.id && (
                <ButtonsListItem
                  key={cart.appliedDiscount.type}
                  active={true}
                  onTouchStart={() => setTouching(true)}
                  onTouchEnd={() => {
                    cart.setAppliedDiscount(null);
                  }}>
                  <Heading level={5} color="var(--colorUIGreen)">
                    {`${
                      cart.appliedDiscount.type === DISCOUNT_TYPE.PERCENTAGE
                        ? cart.appliedDiscount.value
                        : cart.appliedDiscount.value / 100
                    }${
                      cart.appliedDiscount.type === DISCOUNT_TYPE.PERCENTAGE
                        ? '%'
                        : currency.symbol()
                    } ${
                      cart.appliedDiscount.id === MEMBERSHIP_DISCOUNT_ID
                        ? i18n('POS.MembershipDiscount')
                        : i18n('POS.ManualDiscount')
                    }`}
                  </Heading>
                  <Icon src={CheckIcon} color={'var(--colorUIGreen)'} />
                </ButtonsListItem>
              )}
              {offersList.map((offer) => {
                const isActive = cart.appliedDiscount?.id === offer.id;
                const text =
                  (offer.discountType === 'PERCENTAGE'
                    ? offer.discountRate
                    : offer.discountRate / 100) +
                  (offer.discountType === 'PERCENTAGE' ? '%' : currency.symbol()) +
                  ' ' +
                  i18n(offer.nameLang);

                return (
                  <ButtonsListItem
                    key={offer.id}
                    active={isActive}
                    onTouchStart={() => setTouching(true)}
                    onTouchEnd={() => handleTouchEnd(offer)}>
                    <Heading
                      level={5}
                      color={isActive ? 'var(--colorUIGreen)' : 'var(--colorBlueGray1200)'}>
                      {text}
                    </Heading>
                    {isActive && <Icon src={CheckIcon} color={'var(--colorUIGreen)'} />}
                  </ButtonsListItem>
                );
              })}
              {hasRemoveableDiscount && (
                <ButtonsListItem
                  key={'remove'}
                  active={true}
                  onTouchStart={() => setTouching(true)}
                  onTouchEnd={() => {
                    if (forSelectedItems) {
                      cart.selectedItems.setDiscount(0);
                    } else {
                      cart.setAppliedDiscount(null);
                    }
                  }}>
                  <Heading level={5} color="var(--colorUIRed)">
                    {i18n('POS.Clear')}
                  </Heading>
                </ButtonsListItem>
              )}
            </ButtonsList>
          </ListWrapper>
        )}
      </ButtonWrapper>

      {showNumpad && (
        <NumberInput
          onSubmit={handleManualDiscount}
          tabs={[
            { title: i18n('POS.Percentage'), type: 'percentage' },
            { title: i18n('POS.Amount'), type: 'freeform' },
          ]}
          maxValue={cart.total / 100}
          onClose={() => setShowNumpad(false)}
          decimal
        />
      )}
    </>
  );
};

export default React.memo(Discounts);
